/**
 * Map Icons Utility
 * Manages ship icon creation for Leaflet maps
 */
import { formatNum } from '@/util/number';

/* eslint-disable no-undef */

import {
  buildIconConfig,
  scaleIconSize,
  MARKER_ICON_BASE_SIZE,
  HEADING_ICON_BASE_SIZE,
} from '@/util/shipIcons';

/**
 * Create ship icons based on asset type and online status
 * @param {Object} asset - Asset object with massMaty.matyName
 * @param {number} lastGpsTimestamp - Last GPS timestamp in unix format
 * @returns {Object} Object containing shipIcon, shipHeadingIcon, and shipMarkerIcon
 */
export function createShipIcons(asset, lastGpsTimestamp) {
  const currentTime = Math.floor(Date.now() / 1000);
  const isOffline = (currentTime - lastGpsTimestamp) > 600;
  const status = isOffline ? 'offline' : 'online';

  // Resolve the icon set from the shared, user-selectable icon style.
  const { iconConfig, markerIconUrl, headingIconUrl } = buildIconConfig(asset, status);

  const shipIcon = L.icon({
    iconUrl: iconConfig.iconUrl,
    iconSize: iconConfig.iconSize,
    popupAnchor: [0, -41],
    tooltipAnchor: [-50, 50],
    className: 'ship-icon',
  });

  const shipHeadingIcon = L.icon({
    iconUrl: headingIconUrl,
    iconSize: scaleIconSize(HEADING_ICON_BASE_SIZE),
    className: 'heading-icon',
  });

  const shipMarkerIcon = L.icon({
    iconUrl: markerIconUrl,
    iconSize: scaleIconSize(MARKER_ICON_BASE_SIZE),
    className: 'marker-icon',
  });

  return {
    shipIcon,
    shipHeadingIcon,
    shipMarkerIcon,
    isOffline,
    hasHeadingIcon: iconConfig.hasHeadingIcon,
  };
}

/**
 * Create ship tooltip HTML
 * @param {string} shipName - Name of the ship
 * @param {boolean} isOnline - Whether the ship is online
 * @returns {string} HTML string for ship tooltip
 */
export function createShipTooltip(shipName, isOnline) {
  return `<div class="ship-label ${isOnline ? 'ship-online' : 'ship-offline'}">${shipName}</div>`;
}

/**
 * Create dot trail marker popup content
 * @param {Object} data - Marker data point
 * @returns {string} HTML string for popup content
 */
export function createDotTrailPopup(data) {
  const moment = require('moment');
  const dateTime = moment.unix(data.timestamp).format('DD MMM YYYY HH:mm');
  const speedKnots = data.speed ? formatNum((data.speed * 1.94384), 2) : 'N/A';
  const waveHeight = data.waveHeight ? `${formatNum(data.waveHeight, 2)} m` : 'N/A';
  const wavePeriod = data.wavePeriod ? `${formatNum(data.wavePeriod, 1)} s` : 'N/A';
  const seaState = data.seaState || 'N/A';

  return `
    <div style="font-size: 12px; min-width: 180px;">
      <strong style="color: #0d6efd; font-size: 13px;">${dateTime}</strong><br/>
      <table style="width: 100%; margin-top: 6px;">
        <tr>
          <td style="color: #6c757d;">Speed:</td>
          <td style="text-align: right; font-weight: 600;">${speedKnots} kn</td>
        </tr>
        <tr>
          <td style="color: #6c757d;">Heading:</td>
          <td style="text-align: right; font-weight: 600;">${data.heading}°</td>
        </tr>
        <tr>
          <td style="color: #6c757d;">Wave Height:</td>
          <td style="text-align: right; font-weight: 600;">${waveHeight}</td>
        </tr>
        <tr>
          <td style="color: #6c757d;">Wave Period:</td>
          <td style="text-align: right; font-weight: 600;">${wavePeriod}</td>
        </tr>
        <tr>
          <td style="color: #6c757d;">Sea State:</td>
          <td style="text-align: right; font-weight: 600; color: #198754;">${seaState}</td>
        </tr>
      </table>
    </div>
  `;
}

/**
 * Get coordinates from GPS data
 * @param {Object} gpsData - GPS data object
 * @returns {Object} Object with coordinates array and coordinatesData array
 */
export function extractCoordinatesFromGpsData(gpsData) {
  if (!gpsData || !gpsData.data) {
    return { coordinates: [], coordinatesData: [] };
  }

  const dataPointsObj = gpsData.data;
  const dataPointKeys = Object.keys(dataPointsObj);
  const sortedKeys = dataPointKeys.sort((a, b) => Number(a) - Number(b));

  const coordinates = [];
  const coordinatesData = [];

  sortedKeys.forEach((key) => {
    const point = dataPointsObj[key];

    if (point.latitude && point.longitude) {
      coordinates.push([point.latitude, point.longitude]);
      coordinatesData.push({
        latLong: [point.latitude, point.longitude],
        heading: point.track || 0,
        timestamp: point.timestamp || parseInt(key),
        speed: point.speed,
        distance: point.distance,
        waveHeight: point.waveHeight,
        wavePeriod: point.wavePeriod,
        seaState: point.seaState,
      });
    }
  });

  return { coordinates, coordinatesData };
}

/**
 * Get last known position from GPS data
 * @param {Object} gpsData - GPS data object
 * @returns {Array|null} [latitude, longitude] or null if not found
 */
export function getLastLatLngFromGpsData(gpsData) {
  if (!gpsData || !gpsData.data) {
    return null;
  }

  const dataPoints = gpsData.data;
  const keys = Object.keys(dataPoints).sort((a, b) => Number(a) - Number(b));
  const lastPoint = dataPoints[keys[keys.length - 1]];

  if (lastPoint && lastPoint.latitude && lastPoint.longitude) {
    return [lastPoint.latitude, lastPoint.longitude];
  }

  return null;
}

/**
 * Get last track (heading) from GPS data
 * @param {Object} gpsData - GPS data object
 * @returns {number} Track value or 0
 */
export function getLastTrackFromGpsData(gpsData) {
  if (!gpsData || !gpsData.data) return 0;

  const dataPoints = gpsData.data;
  const keys = Object.keys(dataPoints).sort((a, b) => Number(a) - Number(b));
  const lastPoint = dataPoints[keys[keys.length - 1]];

  return lastPoint?.track || 0;
}

/**
 * Check if ship is online based on last GPS timestamp
 * @param {number} lastGpsTimestamp - Last GPS timestamp in unix format
 * @returns {boolean} True if ship is online (within 10 minutes)
 */
export function isShipOnline(lastGpsTimestamp) {
  if (!lastGpsTimestamp) return false;
  const currentTime = Math.floor(Date.now() / 1000);
  return (currentTime - lastGpsTimestamp) <= 600;
}
